import later from "meteor-later"; // https://bunkat.github.io/later/
import moment from "moment"; // https://momentjs.com/
import { AbstractObject } from "../abstract-object";

/**
 * A kérdőív időzítésének osztálya.
 */
export class QuestionnaireSchedule extends AbstractObject {

    /** Az osztály konstruktora. */
    constructor(data?: any) {
        super();
        super.init(data, this);
    }

    public static checkYear(year: string | number): boolean {
        if (typeof year === "number") {
            year = year.toString(10);
        }
        const regexp: RegExp = new RegExp(/^(\*|(\d{4},)*\d{4}|\/\d+|)$/);
        return regexp.test(year);
    }

    public static checkMonth(month: string | number): boolean {
        if (typeof month === "number") {
            month = month.toString(10);
        }
        const regexp: RegExp = new RegExp(/^\*$|^(([1-9]|1[012]),)+([1-9]|1[012])?$|^\/?([1-9]|1[012])$/);
        return regexp.test(month);
    }

    public static checkDay(day: string | number): boolean {
        if (typeof day === "number") {
            day = day.toString(10);
        }
        const regexp: RegExp = new RegExp(/^\*$|^(([1-9]|[12][0-9]|3[01]),)+([1-9]|[12][0-9]|3[01])?$|^\/?([1-9]|[12][0-9]|3[01])$/);
        return regexp.test(day);
    }

    public static checkDayOfWeek(dayOfWeek: string | number): boolean {
        if (typeof dayOfWeek === "number") {
            dayOfWeek = dayOfWeek.toString(10);
        }
        const regexp: RegExp = new RegExp(/^\*$|^wd(:(([1-7],){2,}[1-7]?))?$|^(([1-7],){2,}[1-7]?)$|^\/?[1-7]$/);
        return regexp.test(dayOfWeek);
    }

    public static checkHour(hour: string | number): boolean {
        if (typeof hour === "number") {
            hour = hour.toString(10);
        }
        const regexp: RegExp = new RegExp(/^\*$|^(([0-9]|1[0-9]|2[0-3]),)+([0-9]|1[0-9]|2[0-3])?$|^\/?(([0-9]|1[0-9]|2[0-3]))$/);
        return regexp.test(hour);
    }

    public static checkMinute(minute: string | number): boolean {
        if (typeof minute === "number") {
            minute = minute.toString(10);
        }
        const regexp: RegExp = new RegExp(/^(([0-9]|[1-5][0-9]),)+([0-9]|[1-5][0-9])?$|^\/?(([0-9]|[1-5][0-9]))$/);
        return regexp.test(minute);
    }

    /**
     * Ez állítja össze az időzítés-objektumot, aminek segítségével lekérhetők egy megadott
     * időpőntot megelőző és követő időzítés-időpontok.
     */
    private getSchedule(): later.Schedule {
        let builder: later.RecurrenceBuilder = later.parse.recur();

        if (typeof this.minute === "number") {
            builder = builder.on(this.minute).minute();
        } else if (typeof this.minute === "string") {
            if (this.minute === "*") {
                builder = builder.on(0).minute(); // NINCS olyan, hogy minden percben...
            } else if (this.minute.substring(0, 1) === "/") {
                builder = builder.every(Number.parseInt(this.minute.substring(1))).minute();
            } else if (this.minute.split(",").length > 0) {
                builder = builder.on(
                    ...this.minute.split(",").map(value => Number.parseInt(value))
                ).minute();
            } else if (Number.isInteger(Number.parseInt(this.minute))) {
                builder = builder.on(Number.parseInt(this.minute)).minute();
            } else {
                builder = builder.on(0).minute();
            }
        } else {
            builder = builder.on(0).minute();
        }

        if (typeof this.hour === "number") {
            builder = builder.on(this.hour).hour();
        } else if (typeof this.hour === "string") {
            if (this.hour === "*") {
                // Ezt nem kell külön jelezni a builder-nek, de az ellenőrzés kell.
            } else if (this.hour.substring(0, 1) === "/") {
                builder = builder.every(Number.parseInt(this.hour.substring(1))).hour();
            } else if (this.hour.split(",").length > 0) {
                builder = builder.on(
                    ...this.hour.split(",").map(value => Number.parseInt(value))
                ).hour();
            } else if (Number.isInteger(Number.parseInt(this.hour))) {
                builder = builder.on(Number.parseInt(this.hour)).hour();
            }
        }

        // TODO: WeekDays

        if (typeof this.day === "number") {
            builder = builder.on(this.day).dayOfMonth();
        } else if (typeof this.day === "string") {
            if (this.day === "*") {
                // Ezt nem kell külön jelezni a builder-nek, de az ellenőrzés kell.
            } else if (this.day.substring(0, 1) === "/")
                builder = builder.every(Number.parseInt(this.day.substring(1))).dayOfMonth();
            else if (this.day.split(",").length > 0) {
                builder = builder.on(
                    ...this.day.split(",").map(value => Number.parseInt(value))
                ).dayOfMonth();
            } else if (Number.isInteger(Number.parseInt(this.day))) {
                builder = builder.on(Number.parseInt(this.day)).dayOfMonth();
            }
        }

        if (typeof this.month === "number") {
            builder = builder.on(this.month).month();
        } else if (typeof this.month === "string") {
            if (this.month === "*") {
                // Ezt nem kell külön jelezni a builder-nek, de az ellenőrzés kell.
            } else if (this.month.substring(0, 1) === "/") {
                builder = builder.every(Number.parseInt(this.month.substring(1))).month();
            } else if (this.month.split(",").length > 0) {
                builder = builder.on(
                    ...this.month.split(",").map(value => Number.parseInt(value))
                ).month();
            } else if (Number.isInteger(Number.parseInt(this.month))) {
                builder = builder.on(Number.parseInt(this.month)).month();
            }
        }

        if (typeof this.year === "number") {
            builder = builder.on(this.year).year();
        } else if (typeof this.year === "string") {
            if (this.year === "*") {
                // Ezt nem kell külön jelezni a builder-nek, de az ellenőrzés kell.
            } else if (this.year.substring(0, 1) === "/") {
                builder = builder.every(Number.parseInt(this.year.substring(1))).year();
            } else if (this.year.split(",").length > 0) {
                builder = builder.on(
                    ...this.year.split(",").map(value => Number.parseInt(value))
                ).year();
            } else if (Number.isInteger(Number.parseInt(this.year))) {
                builder = builder.on(Number.parseInt(this.year)).year();
            }
        }

        if (builder.error) {
            throw new Meteor.Error("questionnaire-schedule.schedule-data-error", "Schedule format is invalid!", builder.error.toString());
        }

        // console.log("SCHEDULE: ");
        // console.dir(builder.schedules);

        const schedule: later.Schedule = later.schedule(builder);
        return schedule;
    }

    private _getPrev(date: Date, schedule: later.Schedule): Date {
        let result = schedule.prev(1, date);
        if (result instanceof Date)
            return result;
        else if (Array.isArray(result) && result.filter(_date => _date instanceof Date).length > 0)
            return result[0]
        else
            return null;
    }


    /**
     * Visszaadja az időzítésnek a paraméterben megadott időpontot közvetlenül megelőző időpontját.
     * Ha a paraméter nincs megadva, vagy nincs ilyen előző időzítés, akkor null a visszatérési érték.
     *
     * @param date
     */
    public getPrev(date?: Date): Date {
        return this._getPrev(date || new Date(), this.getSchedule());
    }

    private _getNext(date: Date, schedule: later.Schedule): Date {
        let result = schedule.next(1, date);
        if (result instanceof Date)
            return result;
        else if (Array.isArray(result) && result.filter(_date => _date instanceof Date).length > 0)
            return result[0]
        else
            return null;
    }

    /**
     * Visszaadja az időzítésnek a paraméterben megadott időpontot közvetlenül követő időpontját.
     * Ha a paraméter nincs megadva, vagy nincs ilyen következő időzítés, akkor null a visszatérési érték.
     *
     * @param date
     */
    public getNext(date?: Date): Date {
        return this._getNext(date || new Date(), this.getSchedule());
    }

    /**
     * Ellenőrzi, hogy a megadott időpontban érvényes-e az időzítés az időpontot
     * közvetlenül megelőző időpontja.
     * Azaz a paraméterben megadott időpont az azt megelőző időzítés-időpont és az
     * 'activeTime' által meghatározott időintervallumban van-e.
     *
     * @param date
     */
    public checkPrev(date: Date): boolean {
        // console.log("checkPrev - DATE: " + date.toLocaleString());

        const schedule: later.Schedule = this.getSchedule();

        const prev: Date = this._getPrev(date, schedule);
        // console.log("checkPrev - PREV: " + (prev ? prev.toLocaleString() : prev));

        if (!prev) return false;

        let prevDiff = Math.abs(moment(prev).diff(moment(date), "minutes"));
        // console.log("checkPrev - PREV DIFF: " + prevDiff);

        if (prevDiff > this.activeTime)
            return false;

        return true;
    }

    /**
     * Ellenőrzi, hogy a megadott időpontban érvényes-e az időzítés az időpontot
     * közvetlenül követő időpontja.
     * Azaz a paraméterben megadott időpont az azt követő időzítés-időpont és az
     * 'activeTime' által meghatározott időintervallumban van-e.
     *
     * @param date
     */
    public checkNext(date: Date): boolean {
        // console.log("checkNext - DATE: " + date.toLocaleString());

        const schedule: later.Schedule = this.getSchedule();

        const next: Date = this._getNext(date, schedule);
        // console.log("checkNext - NEXT: " + (next ? next.toLocaleString() : next));

        if (!next) return false;

        let nextDiff = Math.abs(moment(next).diff(moment(date), "minutes"));
        // console.log("checkNext - NEXT DIFF: " + nextDiff);

        if (nextDiff > this.activeTime)
            return false;

        return true;
    }


    /**
     * Az időzítés sorrendje.
     * Ennek segítségével azonosítjuk, hogy egy adott kitöltés már elkezdődött-e ehhez az időzítéshez.
     * - kötelező
     * - típusa integer
     * - értéke nagyobb, mint 0
     * - kérdőíven belül egyedinek kell lennie
     */
    order: number = 0;

    /**
     * Az időzítés év értéke.
     * - kötelező
     * Értékkészlet:
     *  - number: érvényes év négyjegyű számmal megadva (pl. 2019); ilyen esetben
     *            csak az adott évre érvényes az időzítés
     *  - string: karakteresen megadható időzítés
     *      "*" : minden évben
     *      "/x": minden x-edik évben (1970-től kezdve) (x: egész érték)
     */
    year: number | string = "*";

    /**
     * Az időzítés hónap értéke (1: január; 12: december).
     * - kötelező
     * Értékkészlet:
     *  - number: érvényes hónap 1 vagy 2 jegyű számmal megadva (pl. 1, 03, 12); ilyen esetben
     *            csak az adott hónapra érvényes az időzítés
     *  - string: karakteresen megadható időzítés
     *      "*" : minden évben
     *      "n,...": az éven belül a felsorolt hónapokban: (1<=n<=12)
     *      "/x": az év minden x-edik hónapjában 1-től (januártól) kezdve (x: egész érték)
     */
    month: number | string = "*";

    /**
     * Az időzítés hónapon belüli nap értéke (day of month).
     * - kötelező
     * FONTOS! Ha értéke '*'-tól eltérő, akkor a 'dayOfWeek' értéke figyelmen kívül van hagyva!
     * Értékkészlet:
     *  - number: érvényes nap az adott hónapon belül 1 vagy 2 jegyű számmal megadva (pl. 1, 03, 31);
     *            ilyen esetben csak a hónapon belüli adott napra érvényes az időzítés;
     *            ha nincs ilyen nap a hónapban, akkor az időzítés nem évényesülhet (pl. februárban nincs 30.)
     *  - string: karakteresen megadható időzítés
     *      "*" : a hónap minden napján
     *      "n,...": a hónap felsorolt napjain (1<=n<=31; ha az adott hónapban nincs a megadott nap, akkor nincs ilyen időzítés)
     *      "/x": a hónap minden x-edik napján 1-től kezdve
     */
    day: number | string = "*";

    /**
     * Az időzítés héten belüli nap értéke (1: hétfő; 7: vasárnap).
     * - kötelező
     * FONTOS! Ha a 'dayOfMonth' értéke '*'-tól eltérő, akkor a 'dayOfWeek' értéke figyelmen kívül van hagyva!
     * Értékkészlet:
     *  - number: érvényes nap az adott héten belül 1 jegyű számmal megadva (1: hétfő; 7: vasárnap);
     *            ilyen esetben csak a héten belüli adott napra érvényes az időzítés;
     *  - string: karakteresen megadható időzítés
     *      "*" : a hét minden napján
     *      "/x": a hét minden x-szel osztható napján (x: egész érték);
     *      "n,...": a héten a felsorolt napokon (1<=n<=7)
     *	    "wd" : minden munkanapon
     *      "wd:n,...": a hét minden felsorolt munkanapján (1<=n<=7; a az adott munkanap nincs a háten, akkor nincs ilyen időzítés)
     */
    dayOfWeek?: number | string = "*";

    /**
     * Az időzítés óra értéke.
     * - kötelező
     * Értékkészlet:
     *  - number: érvényes óra az adott napon belül 1 vagy 2 jegyű számmal megadva (0-23);
     *            ilyen esetben csak az adott órára érvényes az időzítés;
     *  - string: karakteresen megadható időzítés
     *      "*" : a nap minden órájában
     *      "n,...": a napon belül a felsorolt órákban: (0<=n<=23)
     *      "/x": a nap minden x-szel osztható órájában (x: egész érték);
     */
    hour: number | string = "*";

    /**
     * Az időzítés perc értéke.
     * - kötelező
     * Értékkészlet:
     *  - number: érvényes perc az adott órán belül 1 vagy 2 jegyű számmal megadva (0-59);
     *            ilyen esetben csak az adott percre érvényes az időzítés;
     *  - string: karakteresen megadható időzítés
     *      "n,...": a napon belül a felsorolt órákban: (0<=n<=59)
     *      "/x": a nap minden x-szel osztható órájában (x: egész érték);
     */
    minute: number | string = "0";

    /**
     * Jelzi, hogy minden egyes időzítési periódusban kötelező-e a kérdőív kitöltése.
     * -kötelező (lehet false)
     *
     * ??? - Mit jelent pontosan? Ha 'true' az érték akkor az időintervallumon belül
     *       mindenképpen ki kell-e tölteni? Ha 'false', akkor kaphat olyan értéket a kitöltés,
     *       hogy pl. 'skipped=true'.
     * ??? - Ha van a kérdőíven is kötelezőség jelölése, akkor hogyan írja felül a két érték egymást?
     */
    requiredAlways: boolean = false;

    /**
     * Az időzítés után ennyi perc letelte után a felhasználó már nem kaphatja meg
     * kitöltésre a kérdőívet.
     *
     * Ez alapján számolható ki, hogy a kérdőív kiküldésétől kezdve meddig lehet a
     * kérdőívet kitölteni. Az így kiszámított időpont után a kérdőív-kitöltés erre
     * az időzítésre nem érkezhet be.
     *
     * - kötelező
     * - típusa integer
     * - nagyobb, mint 0
     */
    activeTime: number = 0;
}
